//================================================================
//  Component: MultipleDocumentInput
//================================================================

//  Purpose: A custom input component for uploading multiple documents with React Hook Form.
//  This component allows users to select multiple files, displays their names, and provides a delete option for each file.

//  Properties:
//    - control: {React Hook Form control object for managing form state}
//    - setError: {Function to set form errors}
//    - clearErrors: {Function to clear form errors}
//    - fileUrl: {URL of a file to link to, e.g., a process diagram}
//    - urlLoading: {Boolean indicating if the URL is currently loading, used to show a loading icon}

//  Example:
//    <MultipleDocumentInput
//      control={control}
//      setError={setError}
//      clearErrors={clearErrors}
//      fileUrl="https://example.com/file.pdf"
//      urlLoading={false} // Set to true if the URL is loading
//    ></MultipleDocumentInput>

//================================================================


// Libraries
import React from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';

// Components
import Tooltip from '../../../Components/Tooltip/Tooltip';

// Images
import FileIcon from '../../../Components/Images/Icon_File_Black.svg';
import DeleteIcon from '../../../Components/Images/Icon_DeleteDefault_Red.svg';
import HoverDeleteIcon from '../../../Components/Images/Icon_DeleteHover_Red.svg';
import pendingIcon from '../../../Components/Images/Icon_LoadingFile_Grey.gif';

// CSS

function MultipleDocumentInput({
    control,
    setError,
    clearErrors,
    fileUrl,
    urlLoading = false
}) {

    //------------------------------------------------------
    //  useRef
    //------------------------------------------------------

    // Ref to manage the actual file input element
    const localInputRef = React.useRef(null);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // State to manage selected files and their preview URLs
    const [selectedFilesData, setSelectedFilesData] = React.useState([]);

    // State to manage the currently hovered file ID for delete icon hover effect
    const [hoveredFileId, setHoveredFileId] = React.useState(null);


    //------------------------------------------------------
    //  useEffect
    //------------------------------------------------------

    // Cleanup function to revoke preview URLs when the component unmounts
    React.useEffect(() => {
        return () => {
            selectedFilesData.forEach(data => URL.revokeObjectURL(data.previewUrl));
        };
    }, [selectedFilesData]);

    return (
        <Controller
            name='supportingdoc'
            control={control}
            render={({
                field: { onChange: rhfOnChange, name: controllerFieldName, ref: rhfExternalRef }, // Renamed 'name' to 'controllerFieldName' for clarity
                fieldState: { error }
            }) => {

                //------------------------------------------------------
                //  Functions
                //------------------------------------------------------

                // Function to handle file selection and validation
                const handleFileChange = (e) => {
                    selectedFilesData.forEach(data => URL.revokeObjectURL(data.previewUrl));

                    const filesFromInput = e.target.files;
                    let newSelectedFilesData = [];

                    if (!filesFromInput || filesFromInput.length === 0) {
                        setSelectedFilesData([]);
                        rhfOnChange([]);
                        if (typeof clearErrors === 'function') {
                            clearErrors(controllerFieldName);
                        }
                        if (localInputRef.current) { // Clear native input if selection is cancelled
                            localInputRef.current.value = '';
                        }
                        return;
                    }

                    const maxSize = 30 * 1024 * 1024;
                    let localValidFiles = [];

                    if (typeof clearErrors === 'function') {
                        clearErrors(controllerFieldName);
                    }

                    for (let i = 0; i < filesFromInput.length; i++) {
                        const file = filesFromInput[i];
                        const fileId = `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`;


                        if (file.size > maxSize) {
                            if (typeof setError === 'function') {
                                setError(controllerFieldName, {
                                    type: 'manual',
                                    message: `File '${file.name}' is too large (max 30MB).`,
                                });
                            }
                            continue;
                        }

                        const previewUrl = URL.createObjectURL(file);
                        newSelectedFilesData.push({ file, previewUrl, id: fileId });
                        localValidFiles.push(file);
                    }

                    setSelectedFilesData(newSelectedFilesData);
                    rhfOnChange(localValidFiles);
                };

                // Function to handle file removal
                const handleRemoveFile = (fileIdToRemove) => {
                    const fileToRemove = selectedFilesData.find(data => data.id === fileIdToRemove);
                    if (fileToRemove) {
                        URL.revokeObjectURL(fileToRemove.previewUrl);
                    }

                    const updatedSelectedFilesData = selectedFilesData.filter(data => data.id !== fileIdToRemove);
                    setSelectedFilesData(updatedSelectedFilesData);

                    const updatedFilesForRhf = updatedSelectedFilesData.map(data => data.file);
                    rhfOnChange(updatedFilesForRhf);

                    if (updatedFilesForRhf.length === 0 && localInputRef.current) {
                        localInputRef.current.value = ''; // Reset native input if all files are removed
                    }

                    // Use the 'error' object from fieldState for checking its type
                    if (typeof clearErrors === 'function' && (!error || error.type !== 'manual')) {
                        clearErrors(controllerFieldName);
                    } else if (updatedFilesForRhf.length === 0 && typeof clearErrors === 'function') {
                        clearErrors(controllerFieldName);
                    }
                };

                //------------------------------------------------------
                // Render the component
                //------------------------------------------------------
                return (
                    <div className='flex flex-col gap-3 py-4'>
                        <div className='flex flex-col gap-1 pb-2'>
                            <label className='font-medium'>
                                5. Attach Document(s)
                            </label>
                            <label className='text-[14px] text-slate-500 italic md:w-1/2'>
                                If this relates to improving our workflows, please highlight the specific area you would like to change and attach the document. To view our process diagrams, click {urlLoading ? <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img> :
                                    <Link to={fileUrl} target='_blank' className='text-blue-500 underline'>here</Link>}.
                            </label>
                        </div>

                        {/* Custom File Input Trigger and Info Display */}
                        <div className={`flex items-center border rounded-md p-2 py-1 md:w-1/2 ${error ? 'border-red-500' : 'border-gray-300'}`}>
                            <label
                                htmlFor={controllerFieldName + '-hidden-file-input'}
                                className='cursor-pointer text-[#00a7a4] font-medium py-1 pl-2 pr-3 rounded-md'
                            >
                                Choose Files
                            </label>
                            <div className='border-l border-gray-300 py-[20px] my-[-10px]'></div>

                            {/* This span will display the accurate file count or placeholder */}
                            <span className='ml-1 pl-3'>
                                {
                                    selectedFilesData.length > 0
                                    ? `${selectedFilesData.length} file${selectedFilesData.length > 1 ? 's' : ''} selected`
                                    : 'No file chosen'
                                }
                            </span>
                        </div>

                        <input
                            id={controllerFieldName + '-hidden-file-input'}
                            type='file'
                            multiple={true}
                            className='hidden'
                            onChange={handleFileChange}
                            name={controllerFieldName}
                            ref={(element) => {
                                if (typeof rhfExternalRef === 'function') {
                                    rhfExternalRef(element);
                                } else if (rhfExternalRef && typeof rhfExternalRef === 'object' && rhfExternalRef.hasOwnProperty('current')) {
                                    rhfExternalRef.current = element;
                                }
                                localInputRef.current = element;
                            }}
                            onClick={(e) => (e.target.value = null)}
                        ></input>

                        {/* Previews (List of selected files) */}
                        {
                            selectedFilesData.length > 0 &&
                            <div className='w-full flex flex-col rounded-md border border-[#D8D8D8] md:w-1/2'>
                                {selectedFilesData.map((doc, index) => (
                                    <div key={index} className='flex flex-row justify-between items-center border-b border-b-[#D8D8D8] last:border-0 px-3 py-2'>
                                        <div className='flex flex-row gap-2 items-center'>
                                            <img src={FileIcon} alt='file' className='min-w-[24px] min-h-[24px] w-6 h-6'></img>
                                            <p className='mb-0'>{doc?.file?.name}</p>
                                        </div>
                                        <div>
                                            <Tooltip
                                                message={'Delete File'}
                                                children={
                                                    <div className='Cancel-Icon cursor-pointer' onClick={() => handleRemoveFile(doc.id)} onMouseEnter={() => setHoveredFileId(doc.id)} onMouseLeave={() => setHoveredFileId(null)} >
                                                        <img className='min-w-[24px] min-h-[24px] w-6 h-6' src={hoveredFileId === doc.id ? HoverDeleteIcon : DeleteIcon} alt='Delete-Icon'></img>
                                                    </div>
                                                }
                                            ></Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        }
                        {
                            error && (
                            <label className='font-medium text-[#DE0000] mt-1 text-sm mb-0'>
                                {error.message}
                            </label>
                            )
                        }
                    </div>
                );
            }}
        ></Controller>
    )
}

export default MultipleDocumentInput;